import { useEffect, useRef, useState } from 'react'
import type { GlossaryDictionaryListItem } from '@ai-meeting/shared'
import { ChevronDownIcon } from '@/components/Icons'
import { useUiStrings } from '@/hooks/useUiStrings'
import { fetchGlossaryDictionaries } from '@/services/glossary'

interface GlossaryMultiSelectProps {
  id: string
  selectedIds: string[]
  onChange: (selectedIds: string[]) => void
  disabled?: boolean
}

type LoadState = 'loading' | 'ready' | 'error'

export default function GlossaryMultiSelect({
  id,
  selectedIds,
  onChange,
  disabled = false,
}: GlossaryMultiSelectProps) {
  const { t } = useUiStrings()
  const rootRef = useRef<HTMLDivElement>(null)
  const optionsRef = useRef<HTMLDivElement>(null)
  const [dictionaries, setDictionaries] = useState<GlossaryDictionaryListItem[]>([])
  const [loadState, setLoadState] = useState<LoadState>('loading')
  const [isOpen, setIsOpen] = useState(false)
  const selectedIdSet = new Set(selectedIds)
  const didSeedDefaultsRef = useRef(false)

  const applyLoadedDictionaries = (userDictionaries: GlossaryDictionaryListItem[]) => {
    setDictionaries(userDictionaries)
    setLoadState('ready')
    if (didSeedDefaultsRef.current) return
    didSeedDefaultsRef.current = true
    if (selectedIds.length > 0) return
    const defaultIds = userDictionaries
      .filter((dictionary) => dictionary.isDefault)
      .map((dictionary) => dictionary.id)
    if (defaultIds.length > 0) onChange(defaultIds)
  }

  const loadDictionaries = async () => {
    setLoadState('loading')
    try {
      const response = await fetchGlossaryDictionaries()
      applyLoadedDictionaries(response.user)
    } catch {
      setLoadState('error')
    }
  }

  useEffect(() => {
    let active = true

    void fetchGlossaryDictionaries()
      .then((response) => {
        if (!active) return
        applyLoadedDictionaries(response.user)
      })
      .catch(() => {
        if (active) setLoadState('error')
      })

    return () => {
      active = false
    }
    // Seed defaults once on mount; parent selection starts empty from CreateMeetingModal.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional one-shot seed
  }, [])

  useEffect(() => {
    if (!isOpen) return

    const handlePointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('pointerdown', handlePointerDown)
    return () => document.removeEventListener('pointerdown', handlePointerDown)
  }, [isOpen])

  const hasSelectableDictionaries = loadState === 'ready' && dictionaries.length > 0
  const isDisabled = disabled || !hasSelectableDictionaries

  useEffect(() => {
    if (isDisabled) setIsOpen(false)
  }, [isDisabled])

  useEffect(() => {
    if (!isOpen) return

    const frame = window.requestAnimationFrame(() => {
      const prefersReducedMotion =
        window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
      optionsRef.current?.scrollIntoView?.({
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'nearest',
      })
    })

    return () => window.cancelAnimationFrame(frame)
  }, [isOpen])

  const selectedNames = dictionaries
    .filter((dictionary) => selectedIdSet.has(dictionary.id))
    .map((dictionary) => dictionary.name)
  const summary =
    loadState === 'loading'
      ? t.glossaryLoadingDictionaries
      : loadState === 'error'
        ? t.glossaryLoadDictionariesFailed
        : dictionaries.length === 0
          ? t.glossaryNoDictionaries
          : selectedNames.length > 0
            ? selectedNames.join(', ')
            : t.glossarySelectPlaceholder

  const toggleDictionary = (dictionaryId: string) => {
    onChange(
      selectedIdSet.has(dictionaryId)
        ? selectedIds.filter((id) => id !== dictionaryId)
        : [...selectedIds, dictionaryId]
    )
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Escape' || !isOpen) return
    event.preventDefault()
    event.stopPropagation()
    setIsOpen(false)
    document.getElementById(id)?.focus()
  }

  return (
    <div ref={rootRef} onKeyDown={handleKeyDown}>
      <button
        id={id}
        type="button"
        aria-expanded={isOpen}
        aria-controls={`${id}-options`}
        disabled={isDisabled}
        title={selectedNames.length > 0 ? summary : undefined}
        onClick={() => setIsOpen((current) => !current)}
        className={[
          'input-field flex w-full items-center justify-between gap-3 text-left',
          selectedNames.length === 0 ? 'text-[var(--text-tertiary)]' : 'text-[var(--text-primary)]',
          'disabled:cursor-not-allowed disabled:opacity-60',
        ].join(' ')}
      >
        <span className="min-w-0 flex-1 truncate">{summary}</span>
        <ChevronDownIcon
          className={[
            'h-4 w-4 shrink-0 transition-transform duration-150 motion-reduce:transition-none',
            isOpen ? 'rotate-180' : '',
          ].join(' ')}
        />
      </button>

      {isOpen && (
        <div
          ref={optionsRef}
          id={`${id}-options`}
          role="group"
          aria-label={t.glossary}
          className="mt-1 max-h-48 overflow-y-auto rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] py-1 shadow-[var(--shadow-lg)]"
        >
          {dictionaries.map((dictionary) => (
            <label
              key={dictionary.id}
              className="flex cursor-pointer items-center gap-2.5 px-3 py-2 text-sm text-[var(--text-primary)] hover:bg-[var(--bg-hover)]"
            >
              <input
                type="checkbox"
                checked={selectedIdSet.has(dictionary.id)}
                onChange={() => toggleDictionary(dictionary.id)}
                className="h-4 w-4 shrink-0 rounded border-[var(--border-hover)] accent-[var(--accent)]"
              />
              <span className="min-w-0 truncate" title={dictionary.name}>
                {dictionary.name}
              </span>
            </label>
          ))}
        </div>
      )}

      {loadState === 'error' && (
        <div className="mt-1 flex items-center justify-between gap-3" role="alert">
          <p className="text-xs text-[var(--error)]">{t.glossaryLoadDictionariesFailed}</p>
          <button
            type="button"
            onClick={() => void loadDictionaries()}
            disabled={disabled}
            className="shrink-0 text-xs font-semibold text-[var(--accent)] hover:text-[var(--accent-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {t.glossaryRetry}
          </button>
        </div>
      )}

      <p className="mt-1 text-xs text-[var(--text-tertiary)]">
        {t.glossarySystemAppliedAutomatically}
      </p>
    </div>
  )
}
